import { useMemo, useState } from 'react';

import {
  ArrowDown02Icon,
  BubbleChatQuestionIcon,
  GitBranchIcon,
  Tick02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  Position,
  ReactFlow,
  getStraightPath,
  useNodes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import cn from 'classnames';
import { FieldTypesOptions } from 'constants/field-types-enum';
import { operatorTypes } from 'constants/logic';
import { textWithReferences } from 'helpers/form';
import { useWatch } from 'react-hook-form';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import StatusPill from 'components/Pills/StatusPills';

import LogicEditor from './LogicEditor';

// ─── Constants ───────────────────────────────────────────────────────────────

const NODE_HEIGHT = 120;
const NODE_X = 120;
const VERTICAL_GAP = 48;
// Sequential flow arrow color (always purple)
const ARROW_COLOR = '#8047f2';

// Color palette — one color per trunk group (cycles if > palette length)
const TRUNK_COLORS = [
  '#8047f2', // purple
  '#e5602a', // orange
  '#0ea0a0', // teal
  '#d94f8a', // pink
  '#2a7de5', // blue
  '#7ab83e', // green
];

function getTrunkColor(trunkIndex) {
  return TRUNK_COLORS[trunkIndex % TRUNK_COLORS.length];
}
const FINISH_NODE_ID = 'finish-form';
const FINISH_NODE_EXTRA_GAP = 48;

// Absolute trunk column positions measured leftward from NODE_X.
// Every source group gets its own fixed lane — independent of which node it comes from.
const TRUNK_BASE = 120; // first trunk: 120px left of NODE_X
const TRUNK_STEP = 44; // each extra group moves another 44px further left

// ─── Node height lookup (must match FinishFormNode/QuestionNode inner heights)
// ReactFlow positions are at node top-left. The left handle is at 50% of the
// inner card height. We approximate: outer padding=16, inner card min=72-32=40px,
// handle sits at 50% of inner = 20px from inner top = 16+20=36px from node top.
const HANDLE_OFFSET_Y = 36;

// ─── QuestionNode ─────────────────────────────────────────────────────────────

function QuestionNode({ data, textParams = {} }) {
  const label = typeof data?.label === 'string' ? data.label : '';

  return (
    <div className="w-[280px] h-[120px] bg-neutral-1 border border-neutral-4 rounded-3xl p-4 flex flex-col gap-2 shadow-sm overflow-hidden cursor-pointer">
      <div className="flex items-center gap-2 shrink-0">
        {data?.icon && <HugeiconsIcon icon={data.icon} size="20" className="text-neutral-6" />}
        {data?.type && (
          <span className="text-neutral-7 text-lg font-normal leading-none">{data?.type}</span>
        )}
      </div>
      <div className="flex-1 flex items-center gap-1.5 bg-neutral-2 border border-neutral-4 rounded-2xl p-4 relative u-param-preview">
        <Handle type="target" position={Position.Top} className="!opacity-0 !top-[-1px]" />

        <div className="w-10 h-10 rounded-full bg-neutral-6 flex items-center justify-center shrink-0">
          <span className="text-neutral-1 text-lg font-normal leading-none">{data?.order}</span>
        </div>

        <div
          className="text-neutral-7 text-xl line-clamp-2 overflow-hidden [&_p]:m-0 min-w-0"
          dangerouslySetInnerHTML={{
            __html: textWithReferences(label, textParams, { previewMode: true }, false),
          }}
        />

        <Handle
          type="source"
          position={Position.Bottom}
          id="bottom"
          className="!opacity-0 !bottom-[-8px]"
        />
        {/* Left source handle for logic jumps — at 50% height */}
        <Handle
          type="source"
          position={Position.Left}
          id="left"
          className="!opacity-0 !left-[-8px]"
          style={{ top: '50%' }}
        />
        {/* Left target handle for incoming logic jumps */}
        <Handle
          type="target"
          position={Position.Left}
          id="left-target"
          className="!opacity-0 !left-[-20px]"
          style={{ top: '50%' }}
        />
      </div>
    </div>
  );
}

// ─── FinishFormNode ───────────────────────────────────────────────────────────

function FinishFormNode() {
  return (
    <div className="w-[280px] h-[120px] bg-neutral-1 border border-green-2 rounded-3xl p-4 flex flex-col gap-4 shadow-sm relative overflow-hidden">
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        className="!opacity-0"
        style={{ top: 0 }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left-target"
        className="!opacity-0"
        style={{ top: '50%' }}
      />
      <div className="flex-1 flex items-center gap-1.5 bg-green-1 border border-green-2 rounded-2xl p-4 relative">
        <div className="w-10 h-10 rounded-full bg-green-2 flex items-center justify-center shrink-0">
          <HugeiconsIcon icon={Tick02Icon} size="20" className="text-neutral-1" />
        </div>
        <span className="text-neutral-7 text-xl">Finish form</span>
      </div>
    </div>
  );
}

// ─── SequentialEdge ───────────────────────────────────────────────────────────

function SequentialEdge({ id, sourceX, sourceY, targetX, targetY, target, style }) {
  const [edgePath] = getStraightPath({ sourceX, sourceY, targetX, targetY });
  const refY = target === FINISH_NODE_ID ? 18 : 28;

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        style={{ stroke: ARROW_COLOR, strokeWidth: 1.5, ...style }}
        markerEnd={`url(#sequential-arrow-${id})`}
      />
      <defs>
        <marker
          id={`sequential-arrow-${id}`}
          markerWidth="24"
          markerHeight="24"
          refX="12"
          refY={refY}
          markerUnits="userSpaceOnUse">
          <HugeiconsIcon icon={ArrowDown02Icon} size={24} className="text-primary-4" />
        </marker>
      </defs>
    </>
  );
}

// ─── TrunkEdge ────────────────────────────────────────────────────────────────
//
// Renders the trunk+branch tree for a group of logic jumps from the same source.
// Only the FIRST arm (isFirst=true) renders anything; the rest are silent.
//

function TrunkEdge({ id, sourceX, sourceY, targetX, targetY, label, data }) {
  const rfNodes = useNodes();

  const isFirst = data?.isFirst ?? true;
  const trunkIndex = data?.trunkIndex ?? 0;
  const siblingDescriptors = data?.siblingDescriptors ?? [];

  const color = getTrunkColor(trunkIndex);

  // Trunk column X — absolute position so trunks from different source nodes
  // never land on the same column. NODE_X is the left edge of all question nodes;
  // we park each trunk to the left of that, each group getting its own lane.
  const trunkX = NODE_X - TRUNK_BASE - trunkIndex * TRUNK_STEP;

  // Resolve pixel coords for every sibling arm from node positions
  const siblings = useMemo(() => {
    return siblingDescriptors.map(sib => {
      const node = rfNodes.find(n => n.id === sib.targetId);
      // Target node's left-handle Y = node.position.y + HANDLE_OFFSET_Y
      // Target node's left-handle X = node.position.x  (left edge, handle is at -20px but
      // ReactFlow's targetX will be the measured handle position so we use targetX from props
      // only for the current edge; for siblings we approximate node.position.x)
      const ty = node ? node.position.y + HANDLE_OFFSET_Y : targetY;
      const tx = node ? node.position.x : targetX;
      return { ...sib, targetY: ty, targetX: tx };
    });
  }, [siblingDescriptors, rfNodes, targetY, targetX]);

  if (!isFirst) {
    // Visual already drawn by the first arm — render nothing
    return null;
  }

  if (siblings.length === 0) return null;

  const sortedSibs = [...siblings].sort((a, b) => a.targetY - b.targetY);
  const deepestY = sortedSibs[sortedSibs.length - 1].targetY;
  const r = 8;

  // Trunk path: exit stub from source → turn down → trunk to deepest branch
  const trunkPath = [
    `M ${sourceX} ${sourceY}`,
    `L ${trunkX + r} ${sourceY}`,
    `Q ${trunkX} ${sourceY} ${trunkX} ${sourceY + r}`,
    `L ${trunkX} ${deepestY}`,
  ].join(' ');

  return (
    <>
      {/* Trunk */}
      <path d={trunkPath} fill="none" stroke={color} strokeWidth={1.5} />

      {/* Branch arms */}
      {sortedSibs.map((sib, i) => {
        const isDeepest = i === sortedSibs.length - 1;

        // For non-deepest branches: rounded corner coming off the trunk
        // For deepest: trunk already ends here, just go straight right
        const branchPath = isDeepest
          ? `M ${trunkX} ${sib.targetY} L ${sib.targetX} ${sib.targetY}`
          : [
              `M ${trunkX} ${sib.targetY - r}`,
              `Q ${trunkX} ${sib.targetY} ${trunkX + r} ${sib.targetY}`,
              `L ${sib.targetX} ${sib.targetY}`,
            ].join(' ');

        const markerId = `arrow-trunk-${sib.edgeId}`;

        // Label sits to the LEFT of the trunk column, vertically centred
        // between this branch Y and the previous branch Y (or sourceY for
        // the first branch). This puts each label on the trunk segment that
        // leads INTO this branch — never overlapping another label or a node card.
        const prevY = i === 0 ? sourceY : sortedSibs[i - 1].targetY;
        const labelX = trunkX; // just left of the trunk line
        const labelY = (prevY + sib.targetY) / 2;

        return (
          <g key={sib.edgeId}>
            <path
              d={branchPath}
              fill="none"
              stroke={color}
              strokeWidth={1.5}
              markerEnd={`url(#${markerId})`}
            />
            <defs>
              <marker
                id={markerId}
                markerWidth="8"
                markerHeight="8"
                refX="8"
                refY="4"
                orient="auto"
                markerUnits="userSpaceOnUse">
                <polygon points="0,0 8,4 0,8" fill={color} />
              </marker>
            </defs>

            {sib.label && (
              <EdgeLabelRenderer>
                <div
                  className="absolute pointer-events-none bg-neutral-1 rounded-xl p-2 z-10"
                  style={{
                    transform: `translate(${labelX}px, ${labelY}px) translate(-50%, -50%)`,
                    border: `1.5px solid ${color}`,
                    boxShadow: `0 0 0 1px ${color}20`,
                  }}>
                  <div className="max-w-[120px]">
                    <div className="flex flex-wrap items-stretch gap-2">
                      {sib.label.fieldOrder && (
                        <div className="bg-neutral-2 border border-neutral-4 rounded-lg px-2 py-1 flex items-center gap-2 text-neutral-6 text-lg shrink-0">
                          if
                          <div className="w-6 h-6 rounded-full bg-neutral-6 flex items-center justify-center shrink-0">
                            <span className="text-neutral-1 text-sm font-normal leading-none">
                              {sib.label.fieldOrder}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="bg-neutral-2 border border-neutral-4 rounded-lg px-2 py-1 flex items-center">
                        <span className="text-neutral-7 text-lg">{sib.label.condition}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </EdgeLabelRenderer>
            )}
          </g>
        );
      })}
    </>
  );
}

// ─── Node / Edge type maps ────────────────────────────────────────────────────

const nodeTypes = {
  question: ({ data, textParams = {} }) => <QuestionNode data={data} textParams={textParams} />,
  finishForm: FinishFormNode,
};
const edgeTypes = { sequential: SequentialEdge, trunkJump: TrunkEdge };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildConditionLabel(jump) {
  const valueLabel = jump.value != null ? String(jump.value) : '';
  const operatorValue = jump.operator;
  const operatorLabel =
    operatorValue === 'finish'
      ? 'Go to finish'
      : (operatorTypes?.find(op => op.value === operatorValue)?.symbol ?? operatorValue ?? '=');
  const condition =
    operatorValue === 'finish' ? operatorLabel : `${operatorLabel} ${valueLabel}`.trim();
  const fieldOrder = jump.field != null ? Number(jump.field) : NaN;
  const fromOrder = jump.from != null ? Number(jump.from) : NaN;
  const isDifferentQuestion =
    !Number.isNaN(fieldOrder) && !Number.isNaN(fromOrder) && fieldOrder !== fromOrder;
  if (isDifferentQuestion) {
    return { condition, fieldOrder, fromOrder };
  }
  return { condition };
}

function orderToId(nodes, order) {
  const num = Number(order);
  if (Number.isNaN(num)) return null;
  return nodes.find(n => Number(n.data?.order) === num)?.id ?? null;
}

// ─── LogicTab ─────────────────────────────────────────────────────────────────

const LogicTab = ({
  control,
  register,
  setValue,
  blockBefore = 0,
  appendLogic,
  removeLogic,
  logicJumpsFields,
  textParams = {},
  locale = 'en_GB',
  fields = [],
  sidebarCollapsed = false,
}) => {
  const logicJumps = useWatch({ control, name: 'logic_jumps' }) ?? [];
  const [questionDetailModal, setQuestionDetailModal] = useState({ open: false, index: null });

  const handleRemoveLogic = index => removeLogic(index);

  const handleNodeClick = (_, node) => {
    if (node.type !== 'question' || node.data?.order == null) return;
    const index = fields.findIndex(f => Number(f?.order) === Number(node.data.order));
    if (index >= 0) {
      setQuestionDetailModal({ open: true, index });
    }
  };

  const closeQuestionDetailModal = () => setQuestionDetailModal({ open: false, index: null });

  const nodes = useMemo(() => {
    const questionNodes = fields.map((field, idx) => ({
      id: `node-${field.id ?? field.key ?? idx}`,
      type: 'question',
      data: {
        label: field.text ?? '',
        order: field.order ?? idx + 1,
        type: FieldTypesOptions?.find(f => f.key === field.type)?.title,
        icon: FieldTypesOptions?.find(f => f.key === field.type)?.icon,

        isStatement: field.type === 'statement',
      },
      position: { x: NODE_X, y: idx * (NODE_HEIGHT + VERTICAL_GAP) },
      draggable: false,
      selectable: false,
    }));

    if (questionNodes.length === 0) return questionNodes;

    return [
      ...questionNodes,
      {
        id: FINISH_NODE_ID,
        type: 'finishForm',
        data: {},
        position: {
          x: NODE_X,
          y: questionNodes.length * (NODE_HEIGHT + VERTICAL_GAP) + FINISH_NODE_EXTRA_GAP,
        },
        draggable: false,
        selectable: false,
      },
    ];
  }, [fields]);

  const edges = useMemo(() => {
    if (!nodes.length) return [];

    const result = [];
    const questionNodes = nodes.filter(n => n.id !== FINISH_NODE_ID);
    const finishNode = nodes.find(n => n.id === FINISH_NODE_ID);

    // ── Sequential edges ──────────────────────────────────────────────────────
    for (let i = 0; i < questionNodes.length - 1; i++) {
      result.push({
        id: `seq-${questionNodes[i].id}-${questionNodes[i + 1].id}`,
        source: questionNodes[i].id,
        target: questionNodes[i + 1].id,
        sourceHandle: 'bottom',
        type: 'sequential',
      });
    }
    if (finishNode && questionNodes.length > 0) {
      const last = questionNodes[questionNodes.length - 1];
      result.push({
        id: `seq-${last.id}-${FINISH_NODE_ID}`,
        source: last.id,
        target: FINISH_NODE_ID,
        sourceHandle: 'bottom',
        targetHandle: 'top',
        type: 'sequential',
      });
    }

    // ── Logic jump edges ──────────────────────────────────────────────────────
    // 1. Parse valid jumps
    const validJumps = [];
    (logicJumps ?? []).forEach((jump, index) => {
      if (!jump) return;
      const fromOrder = jump.from != null ? Number(jump.from) : NaN;
      const toOrder = jump.to != null ? Number(jump.to) : NaN;
      if (Number.isNaN(fromOrder) || fromOrder === toOrder) return;

      const sourceId = orderToId(nodes, fromOrder);
      if (!sourceId) return;

      const isFinishForm = toOrder === -1;
      const targetId = isFinishForm ? FINISH_NODE_ID : orderToId(nodes, toOrder);
      if (!targetId) return;

      validJumps.push({
        sourceId,
        targetId,
        label: buildConditionLabel(jump),
        edgeId: `logic-jump-${index}-${sourceId}-${targetId}`,
        isFinishForm,
      });
    });

    // 2. Group by sourceId (Map preserves order)
    const grouped = new Map();
    validJumps.forEach(j => {
      if (!grouped.has(j.sourceId)) grouped.set(j.sourceId, []);
      grouped.get(j.sourceId).push(j);
    });

    // 3. Emit edges — one per arm, first arm draws the full tree
    let trunkIndex = 0;
    grouped.forEach((arms, sourceId) => {
      // Sibling descriptors: static data (coords resolved at render via useNodes)
      const siblingDescriptors = arms.map(a => ({
        edgeId: a.edgeId,
        targetId: a.targetId,
        label: a.label,
      }));

      arms.forEach((arm, armIdx) => {
        result.push({
          id: arm.edgeId,
          source: sourceId,
          target: arm.targetId,
          sourceHandle: 'left',
          targetHandle: 'left-target',
          type: 'trunkJump',
          label: arm.label,
          data: {
            trunkIndex,
            isFirst: armIdx === 0,
            siblingDescriptors,
            isFinishForm: arm.isFinishForm,
          },
          zIndex: 10,
        });
      });

      trunkIndex++;
    });

    return result;
  }, [nodes, logicJumps]);

  return (
    <>
      <div className="flex px-6 py-8 gap-8">
        <div
          className={cn(
            'flex flex-col gap-8',
            sidebarCollapsed
              ? 'min-[1140px]:flex-1 min-[1140px]:min-w-0 min-[1140px]:max-w-[calc(13rem+32%+3rem)]'
              : 'w-1/3',
            'max-[1140px]:flex-1 max-[1140px]:min-w-0',
          )}>
          <LogicEditor
            control={control}
            register={register}
            setValue={setValue}
            blockBefore={blockBefore}
            appendLogic={appendLogic}
            removeLogic={handleRemoveLogic}
            logicJumpsFields={logicJumpsFields}
            textParams={textParams}
            locale={locale}
            fields={fields}
          />
        </div>
        <div className="flex-1 max-[1140px]:min-w-0" />
      </div>

      <div className="fixed top-[calc(7.4rem+10rem+3rem)] right-6 bottom-6 left-[calc(18rem+32%+3rem)] max-[1140px]:left-[calc(7rem+46%)] z-20 cursor-not-allowed bg-neutral-1 rounded-3xl overflow-hidden shadow-sm border border-neutral-4 flex flex-col transition-[left] duration-200">
        <div className="p-4 flex-shrink-0 flex-wrap border-b border-neutral-4 flex items-center justify-between">
          <span className="text-neutral-6 text-lg">
            Logic flow preview — use the editor on the left to manage jumps
          </span>
          <div className="flex items-center gap-4">
            <div className="relative">
              <HugeiconsIcon icon={GitBranchIcon} className="cursor-pointer" size="20" />
              <span
                className={
                  (logicJumps?.length || 0) > 0
                    ? 'absolute -top-1 -right-1 bg-primary-4 text-neutral-1 text-xs rounded-full w-5 h-5 flex items-center justify-center shrink-0'
                    : 'absolute -top-1 -right-1 bg-orange-2 text-neutral-1 text-xs rounded-full w-5 h-5 flex items-center justify-center shrink-0'
                }>
                {logicJumps?.length || 0}
              </span>
            </div>
            <span className="text-neutral-6 text-lg">Logic jumps configured</span>
          </div>
        </div>
        <div className="flex-1 min-h-0 w-full">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodeClick={handleNodeClick}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            panOnDrag
            zoomOnScroll
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}>
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
      </div>

      <AnimatedModal
        visible={questionDetailModal.open}
        onClose={closeQuestionDetailModal}
        title="Question details"
        icon={BubbleChatQuestionIcon}
        oldIconLibrary={false}
        small>
        {questionDetailModal.index != null &&
          fields[questionDetailModal.index] &&
          (() => {
            const field = fields[questionDetailModal.index];
            const typeOption = FieldTypesOptions?.find(f => f.key === field.type);
            const typeTitle = typeOption?.title ?? field.type ?? '';
            const typeIcon = typeOption?.icon;
            const text = field.text ?? '';
            return (
              <div className="flex flex-col gap-4 p-1">
                <div className="text-neutral-7 text-xl bg-neutral-2 border border-neutral-4 rounded-2xl p-4 min-h-[60px] [&_p]:m-0 flex items-start gap-2">
                  <span className="w-10 h-10 rounded-full bg-neutral-6 flex items-center justify-center shrink-0 text-neutral-1 text-lg font-normal">
                    {field.order ?? questionDetailModal.index + 1}
                  </span>
                  <div className="flex flex-col gap-2">
                    {typeIcon && (
                      <StatusPill icon={typeIcon} text={typeTitle} iconSize="16" status="neutral" />
                    )}
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          textWithReferences(text, textParams, { previewMode: true }, false) ||
                          '<span class="text-neutral-5">No text</span>',
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })()}
      </AnimatedModal>
    </>
  );
};

export default LogicTab;
